import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import React, { forwardRef, memo } from 'react'
import { cn } from '@/lib/cn'
import { headingStyle } from './styles'

type IHeadingProps = VariantProps<typeof headingStyle> &
  React.ComponentPropsWithoutRef<'h1'> & {
    as?: React.ElementType
  }

const MappedHeading = memo(
  forwardRef<HTMLHeadingElement, IHeadingProps>(function MappedHeading(
    { size, className, isTruncated, bold, underline, strikeThrough, sub, italic, highlight, ...props },
    ref
  ) {
    const variantClassName = headingStyle({ size, isTruncated, bold, underline, strikeThrough, sub, italic, highlight } as NonNullable<Parameters<typeof headingStyle>[0]>)
    const finalClassName = cn(variantClassName, className)

    switch (size) {
      case '5xl':
      case '4xl':
      case '3xl':
      case 'large-title':
        return <h1 className={finalClassName} {...props} ref={ref} />
      case '2xl':
      case 'title-1':
        return <h2 className={finalClassName} {...props} ref={ref} />
      case 'xl':
      case 'title-2':
        return <h3 className={finalClassName} {...props} ref={ref} />
      case 'lg':
      case 'title-3':
        return <h4 className={finalClassName} {...props} ref={ref} />
      case 'md':
        return <h5 className={finalClassName} {...props} ref={ref} />
      case 'sm':
      case 'xs':
        return <h6 className={finalClassName} {...props} ref={ref} />
      default:
        return <h4 className={finalClassName} {...props} ref={ref} />
    }
  })
)

const Heading = memo(
  forwardRef<HTMLHeadingElement, IHeadingProps>(function Heading(
    { className, size = 'title-3', as: AsComp, ...props },
    ref
  ) {
    if (AsComp) {
      const { isTruncated, bold, underline, strikeThrough, sub, italic, highlight } = props
      const variantClassName = headingStyle({ size, isTruncated, bold, underline, strikeThrough, sub, italic, highlight } as NonNullable<Parameters<typeof headingStyle>[0]>)
      return <AsComp className={cn(variantClassName, className)} {...props} ref={ref} />
    }

    return <MappedHeading className={className} size={size} ref={ref} {...props} />
  })
)

Heading.displayName = 'Heading'

export { Heading }
